
export default function PricingSection() {
  const plans = [
    {
      duration: '2 Hours',
      price: '15,000',
      description: 'Perfect for short trips and airport transfers',
      features: [
        'Tokyo 23 wards coverage',
        'Basic navigation support',
        'Parking assistance',
        'Light sightseeing guidance'
      ],
      popular: false
    },
    {
      duration: 'Half Day (4 Hours)',
      price: '30,000',
      description: 'Great for sightseeing and shopping',
      features: [
        'Greater Tokyo area coverage',
        'Tourist spot guidance',
        'Parking & highway support',
        'Restaurant reservation support',
        'Photo spot assistance'
      ],
      popular: true
    },
    {
      duration: 'Full Day (8 Hours)',
      price: '50,000',
      description: 'Enjoy Tokyo to the fullest all day long',
      features: [
        'Kanto region wide coverage',
        'Full sightseeing support',
        'Multiple destination tour',
        'Dining & rest spot suggestions',
        'Photo spot assistance',
        'Flexible schedule adjustment'
      ],
      popular: false
    }
  ];

  return (
    <section id="pricing" className="py-20 bg-gradient-to-b from-black via-gray-900 to-black">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-amber-500/10 border border-amber-500/30 rounded-full px-6 py-2 mb-6">
            <span className="text-amber-400 text-sm font-semibold uppercase tracking-wider">Pricing</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Pricing Plans
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Clear pricing based on your usage time.<br className="hidden md:block" />
            No hidden fees — straightforward and transparent.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 transition-all duration-300 hover:shadow-2xl ${
                plan.popular
                  ? 'border-2 border-amber-500 hover:shadow-amber-500/30 scale-105'
                  : 'border border-gray-700/50 hover:border-amber-500/50 hover:shadow-amber-500/10'
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black px-6 py-2 rounded-full text-sm font-bold whitespace-nowrap shadow-lg">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Duration */}
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">{plan.duration}</h3>
                <p className="text-gray-400 text-sm">{plan.description}</p>
              </div>

              {/* Price */}
              <div className="text-center mb-8">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-gray-400 text-lg">¥</span>
                  <span className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-600">
                    {plan.price}
                  </span>
                </div>
                <p className="text-gray-400 text-sm mt-2">Tax included</p>
              </div>

              {/* Features */}
              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-500/20 flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-amber-400 text-sm"></i>
                    </div>
                    <span className="text-gray-300 text-sm leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <a
                href="https://line.me/ti/p/~yokota0809"
                target="_blank"
                rel="noopener noreferrer"
                className={`block text-center py-4 rounded-xl font-bold transition-all duration-300 whitespace-nowrap ${
                  plan.popular
                    ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-black hover:from-amber-400 hover:to-yellow-500 shadow-lg hover:shadow-amber-500/50'
                    : 'bg-white/10 text-white border border-white/30 hover:bg-white/20'
                }`}
              >
                Book This Plan
              </a>
            </div>
          ))}
        </div>

        {/* Extension Fee */}
        <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-amber-500/30 rounded-2xl p-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-yellow-600">
                <i className="ri-time-line text-3xl text-black"></i>
              </div>
              <div>
                <h4 className="text-xl font-bold text-white mb-1">Extension Fee</h4>
                <p className="text-gray-300 text-sm">For time exceeding your booked plan</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <div className="flex items-baseline gap-1 justify-center md:justify-end">
                <span className="text-gray-400">¥</span>
                <span className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-600">
                  8,000
                </span>
                <span className="text-gray-400 text-lg">/ hour</span>
              </div>
              <p className="text-gray-400 text-sm mt-1">Calculated in 30-minute increments</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <a
            href="https://line.me/ti/p/~yokota0809"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-500 to-yellow-600 text-black px-12 py-5 rounded-full font-bold text-lg hover:from-amber-400 hover:to-yellow-500 transition-all duration-300 shadow-lg hover:shadow-amber-500/50 whitespace-nowrap"
          >
            <i className="ri-line-fill text-2xl"></i>
            <span>Book &amp; Inquire via LINE</span>
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
